// src/lib/resas-v2-source.js — Wagon M (Lot M2 → amendé M3.0)
//
// Transformation PURE d'une row `reservations` V2 (relationnel Supabase) vers
// le format « résa-like KV » produit par les parsers CSV (csv-airbnb.js /
// csv-booking.js) puis consommé par calc.js (montant, menage, taxe, nuits…).
//
// ⚠️ MODULE PUR — ZÉRO I/O. Aucun appel Supabase / réseau ici. L'I/O (lecture
// des rows, injection du forfait ménage par logement, édition par résa) viendra
// au câblage M3. Ce module NE modifie AUCUN comportement de l'app : il n'est
// importé nulle part dans le flux facturation tant que M3 n'a pas câblé.
//
// SÉMANTIQUE DU MONTANT (rétro-ingénierie factures réelles P-2026 + API Beds24,
// cf. brief M2 — NE PAS dévier) :
//   - Airbnb  : montant net hôte = total_amount − ota_commission ; taxe = 0
//               (Airbnb collecte et reverse seul la taxe de séjour).
//   - Booking : montant net hôte = total_amount − ota_commission ; taxe = 0
//               (Booking ≡ Airbnb : la taxe de séjour est collectée et reversée
//               par la plateforme, elle ne transite PAS par l'hôte — vérité
//               terrain = relevé virement Booking, décision Dimitri). city_tax
//               reste lue (warning F3 informatif) mais N'EST PAS déduite du net.
//               ota_commission Beds24 agrège déjà commission + payment charge.
//   - Si raw_payload.invoiceItems présent (futur sync enrichi) : préférer la
//     décomposition invoiceItems, fallback colonnes. subType 10 = « City tax ».
//
// Le forfait ménage n'a pas de source V2 fiable : il est résolu par
// resolveMenage (override manuel > item ménage OTA > forfait logement > 0).
//
// AMENDEMENTS M3.0 (spec validée Dimitri + pessimist, probe API 12/06) :
//   A. Annulées → EXCLUES de la facturation, remontées dans skipped[] pour
//      décision manuelle (montants V2 d'une cancelled = PÉRIMÉS, non fiables).
//   B. Contrat F1 : un mois ne plante jamais (transform throw → skipped).
//   C. Contrat F4 : source tierce non validée (≠ airbnb/booking/direct) → skipped
//      (pas de calcul Booking-like silencieux sans preuve facture).
//   D. Hiérarchie ménage déterministe via resolveMenage.
//   E. Contrat F3 : warn divergence taxe items vs colonne (ctx sans PII).
//   F. Contrat F5 : nightsBetween mutualisé depuis resa-invoice-month.

const {
  computeInvoiceMonthByNights,
  nightsBetween,
} = require('./resa-invoice-month')

// Arrondi monétaire 2 décimales — même mécanique que le flux CSV / calc.js
// (Math.round(x*100)/100). Pas de toFixed (string), pas de bricolage.
function round2(n) {
  return Math.round((Number(n) || 0) * 100) / 100
}

// Garde anti-NaN : exige un nombre fini, sinon throw explicite (jamais de NaN
// silencieux qui contaminerait une facture — chemin argent en périphérie).
function requireFinite(value, label, ctx) {
  const n = Number(value)
  if (!Number.isFinite(n)) {
    throw new Error(
      `resas-v2-source: ${label} non-fini (${value}) sur résa ${ctx || '?'}`
    )
  }
  return n
}

// Sources OTA dont la sémantique montant est PROUVÉE par facture réelle.
const VALIDATED_SOURCES = new Set(['airbnb', 'booking'])
// Statuts (colonne OU raw_payload.status) qui signent une annulation Beds24.
// Aligné sur mapBeds24Status (api/_lib/beds24/mappers.js) : cancelled/deleted/
// black → cancelled. Un test de parité verrouille cet alignement.
const CANCELLED_STATUSES = new Set(['cancelled', 'deleted', 'black'])

// ── invoiceItems (futur sync enrichi) ──
// Structure réelle d'un invoiceItem Beds24 :
//   { type:'charge', subType:8|9|10|1, description, amount, lineTotal }
//   subType 8  = commission OTA (Airbnb)
//   subType 9  = hébergement (Booking)
//   subType 10 = City tax (taxe de séjour)
//   subType 1  = divers
// Retourne null si invoiceItems absent/vide (→ fallback colonnes).
function extractCityTaxFromItems(invoiceItems) {
  if (!Array.isArray(invoiceItems) || invoiceItems.length === 0) return null
  let cityTax = 0
  let found = false
  for (const it of invoiceItems) {
    if (!it) continue
    if (Number(it.subType) === 10) {
      const v = it.lineTotal != null ? it.lineTotal : it.amount
      cityTax += Math.abs(Number(v) || 0)
      found = true
    }
  }
  return found ? round2(cityTax) : null
}

// ── extractMenageFromItems(invoiceItems) : number | null ──
// Cherche un item ménage OTA dans invoiceItems.
// ⚠️ TODO — le subType ménage Beds24 est INCONNU à ce jour (subTypes observés :
// 8=héb. Airbnb, 9=héb. Booking, 10=City tax, 1=divers ; AUCUNE ligne ménage
// jamais observée — la property sondée est une sous-loc sans frais de ménage).
// On matche donc par DESCRIPTION (/m[ée]nage|cleaning/i) sur un item de type
// 'charge', en attendant d'identifier le subType réel.
// À identifier le jour où l1-l7 passent Beds24 (Lot 0) — NE PAS DEVINER de
// subType ici (faux positif = facturation fantôme). En cas de doute → null
// (resolveMenage retombe alors sur le forfait).
function extractMenageFromItems(invoiceItems) {
  if (!Array.isArray(invoiceItems) || invoiceItems.length === 0) return null
  for (const it of invoiceItems) {
    if (!it) continue
    const type = String(it.type || '').toLowerCase()
    if (type !== 'charge') continue
    const desc = String(it.description || '')
    if (/m[ée]nage|cleaning/i.test(desc)) {
      const v = it.lineTotal != null ? it.lineTotal : it.amount
      const n = Math.abs(Number(v) || 0)
      if (Number.isFinite(n) && n > 0) return round2(n)
    }
  }
  return null
}

// ── classifyStatus(row) : 'facturable' | 'cancelled' | 'incoherent' ──
// Garde défensive contre les statuts MENTEURS (cas réels prouvés en prod) :
// la colonne `status` et `raw_payload.status` peuvent diverger. En cas de
// doute, on ne facture JAMAIS.
// ⚠️ LIMITE (finding N4 pessimist) : cette garde NE couvre PAS le cas
// concordant-mais-périmé — colonne ET raw disent confirmed alors que l'OTA a
// annulé sans que la sync l'ait vu (fantôme réel 87160782). Seul le fix-sync
// annulations (apply auto + cron source) ferme ce trou : PRÉREQUIS DUR du
// câblage M3.2, cette garde n'est qu'une ceinture.
//   - L'un OU l'autre dit cancelled/black → 'cancelled' (décision manuelle).
//   - Sinon, divergence colonne↔raw MAPPÉ (valeurs non vides différentes) →
//     'incoherent' (skipped, jamais facturée).
//   - Sinon → 'facturable'.
//
// ⚠️ La comparaison se fait APRÈS mapping du raw : `raw_payload.status` est le
// statut Beds24 BRUT ('new'…) que le mapper sync (`mapBeds24Status`) mappe sur
// la colonne 'confirmed'. Comparer 'confirmed' au littéral 'new' déclarait
// 100 % des rows synced « incoherent » et VIDAIT toute la projection — bug réel
// attrapé au premier test de bascule lc1 (2026-06-13).
const RAW_ACTIVE_STATUSES = new Set(['new', 'request', 'confirmed', 'inquiry'])

function mapRawStatus(rawStatus) {
  if (!rawStatus) return null
  if (CANCELLED_STATUSES.has(rawStatus)) return 'cancelled'
  if (RAW_ACTIVE_STATUSES.has(rawStatus)) return 'confirmed'
  return rawStatus // statut inconnu : comparé tel quel (divergence → incoherent)
}

function classifyStatus(row) {
  const colStatus = String(row.status || '').toLowerCase().trim()
  const rawStatus = String((row.raw_payload || {}).status || '')
    .toLowerCase()
    .trim()
  if (CANCELLED_STATUSES.has(colStatus) || CANCELLED_STATUSES.has(rawStatus)) {
    return 'cancelled'
  }
  // Divergence colonne↔raw mappé quand les deux sont renseignés et différents.
  const mappedRaw = mapRawStatus(rawStatus)
  if (colStatus && mappedRaw && colStatus !== mappedRaw) {
    return 'incoherent'
  }
  return 'facturable'
}

// ── buildCtx(row) : contexte SANS PII (RGPD) pour skipped[] / logs ──
// Jamais de nom/email ici : ids stables + dates uniquement.
function buildCtx(row) {
  const raw = row.raw_payload || {}
  return {
    id: row.id != null ? row.id : null,
    external_id: row.external_id != null ? row.external_id : null,
    apiReference: raw.apiReference != null ? String(raw.apiReference) : null,
    source: (row.source || '').toLowerCase() || null,
    checkIn: row.start_date || null,
    checkOut: row.end_date || null,
  }
}

// ── resolveMenage(row, { cleaningFeeDefault, override }) ──
// Hiérarchie DÉTERMINISTE FIGÉE (décision Dimitri) :
//   override manuel > invoiceItem ménage OTA > forfait logement > 0
// Retourne { menage, menage_source } avec menage_source ∈
//   'override' | 'ota_item' | 'forfait' | 'missing'.
// L'option `override` sera passée par le câblage M3.2 (défaut undefined).
function resolveMenage(row, opts = {}) {
  const { cleaningFeeDefault, override } = opts
  // 1) Override manuel (priorité absolue) — doit être un nombre fini.
  if (override != null) {
    const n = Number(override)
    if (Number.isFinite(n)) {
      return { menage: round2(n), menage_source: 'override' }
    }
  }
  // 2) Item ménage OTA (match par description ; subType inconnu — cf. TODO).
  const invoiceItems = (row && row.raw_payload && row.raw_payload.invoiceItems) || null
  const fromItems = extractMenageFromItems(invoiceItems)
  if (fromItems != null) {
    return { menage: fromItems, menage_source: 'ota_item' }
  }
  // 3) Forfait logement (injecté par l'appelant).
  if (cleaningFeeDefault != null) {
    const n = Number(cleaningFeeDefault)
    if (Number.isFinite(n)) {
      return { menage: round2(n), menage_source: 'forfait' }
    }
  }
  // 4) Absent → 0 + missing (comportement M2 conservé).
  return { menage: 0, menage_source: 'missing' }
}

// a) transformV2Resa(row, { cleaningFeeDefault, override })
// → { voyageur, plateforme, nuits, montant, menage, menage_source, taxe,
//     checkIn, checkOut, date, airbnbCode } (+ bookingRef pour Booking).
// CONTRAT UNITAIRE : garde son throw (montant non-fini, etc.). C'est
// getResasForMonth qui enveloppe (F1) pour qu'un mois ne plante jamais.
function transformV2Resa(row, opts = {}) {
  if (!row || typeof row !== 'object') {
    throw new Error('resas-v2-source: row absente')
  }

  const source = (row.source || '').toLowerCase()
  const plateforme = source || 'direct'
  const ctx = row.id || row.external_id || (row.raw_payload || {}).apiReference || '?'

  const checkIn = row.start_date || null
  const checkOut = row.end_date || null

  // Nuits : helper mutualisé (F5) — start inclus, end exclu, fallback 1.
  const nuits = nightsBetween(checkIn, checkOut)

  const raw = row.raw_payload || {}
  const invoiceItems = raw.invoiceItems || null

  const totalAmount = requireFinite(row.total_amount, 'total_amount', ctx)
  const otaCommission = requireFinite(
    row.ota_commission != null ? row.ota_commission : 0,
    'ota_commission',
    ctx
  )
  // city_tax colonne (fallback) ; invoiceItems subType 10 prioritaire si présent.
  const cityTaxCol = requireFinite(
    row.city_tax != null ? row.city_tax : 0,
    'city_tax',
    ctx
  )
  const cityTaxFromItems = extractCityTaxFromItems(invoiceItems)

  // F3 — warn si les items divergent de la colonne (sans PII).
  if (
    cityTaxFromItems != null &&
    Math.abs(cityTaxFromItems - cityTaxCol) > 0.01
  ) {
    // eslint-disable-next-line no-console
    console.warn(
      '[resas-v2-source] divergence city_tax items vs colonne',
      {
        id: row.id != null ? row.id : null,
        apiReference: raw.apiReference != null ? String(raw.apiReference) : null,
        items: cityTaxFromItems,
        colonne: cityTaxCol,
        checkIn,
        checkOut,
      }
    )
  }

  const cityTax = cityTaxFromItems != null ? cityTaxFromItems : cityTaxCol

  // ── Sémantique montant net hôte par plateforme ──
  // (F4 : filterFacturable garantit en amont que seules airbnb/booking
  //  arrivent ici ; ce switch reste explicite par plateforme.)
  let montant
  let taxe
  if (source === 'airbnb') {
    // Airbnb reverse la taxe seul → taxe = 0, pas déduite du net.
    montant = round2(totalAmount - otaCommission)
    taxe = 0
  } else {
    // Booking : la taxe de séjour est collectée et reversée par Booking
    // (≡ Airbnb) — elle ne transite PAS par l'hôte, donc NON déduite du net et
    // taxe = 0. cityTax reste lue ci-dessus (warning F3 informatif), jamais
    // soustraite. Vérité terrain = relevé virement Booking (décision Dimitri).
    montant = round2(totalAmount - otaCommission)
    taxe = 0
  }

  if (!Number.isFinite(montant)) {
    throw new Error(`resas-v2-source: montant non-fini sur résa ${ctx}`)
  }

  // ── Ménage (hiérarchie déterministe) ──
  const { menage, menage_source } = resolveMenage(row, opts)

  // ── Identifiants stables (mêmes conventions que le KV) ──
  // Airbnb : apiReference = code HMxxxxxxxx (identique KV airbnbCode).
  // Booking : apiReference = id numérique Booking (→ bookingRef).
  const apiReference =
    raw.apiReference != null ? String(raw.apiReference).trim() : ''
  const airbnbCode = source === 'airbnb' && apiReference ? apiReference : null
  const bookingRef = source === 'booking' && apiReference ? apiReference : null

  const out = {
    // id table reservations (additif Lot 1 V2-edit-montant) : nécessaire au gel
    // de la facture voyageur V par résa (findBlockingVoyageurInvoice). null si
    // la row n'a pas d'id (fixtures de test).
    id: row.id != null ? row.id : null,
    voyageur:
      row.voyageur ||
      [row.guest_first_name, row.guest_last_name].filter(Boolean).join(' ') ||
      '',
    plateforme,
    nuits,
    montant,
    menage,
    menage_source,
    taxe,
    checkIn,
    checkOut,
    date: checkIn,
    airbnbCode,
    // ── Champs ORIGINE additifs (Lot 0 Statistiques proprio) ──
    // ADDITIFS PURS : ZÉRO champ existant ci-dessus modifié. Servent au moteur
    // d'agrégation stats à reconstituer le CA BRUT OTA ancré sur le vrai brut
    // (mode UNIQUE `origine_plus_delta`, décision Dimitri) :
    //   caBrut = ca_brut_origine + (montant − montant_origine)
    // où `montant` peut être corrigé par applyMontantOverrides (Lot 1).
    //   ca_brut_origine = brut OTA payé par le voyageur (total_amount).
    //   ota_commission  = commission OTA (= brut − net − taxe, identité vérifiée).
    //   montant_origine = net hôte d'origine (= `montant` avant tout override).
    // Identité garantie ici : ca_brut_origine − montant − taxe === ota_commission.
    //   - Airbnb : taxe=0 → brut − net = ota. ✓
    //   - Booking: taxe=0 (city_tax externe, non déduite) → brut − net = ota. ✓
    //     (ex. témoin lc2 juin : 95,89 − 81,05 − 0 = 14,84.)
    ca_brut_origine: round2(totalAmount),
    ota_commission: round2(otaCommission),
    montant_origine: montant,
    // ── nbVoyageurs additif (Lot 2 Statistiques proprio) ──
    // ADDITIF PUR. = (numAdult||0)+(numChild||0) lu sur raw_payload.
    // null (jamais 0) si raw_payload absent OU ni numAdult ni numChild
    // numérique (donnée non renseignée → exclue de la moyenne, pas comptée 0).
    nbVoyageurs: extractNbVoyageurs(raw),
  }
  if (bookingRef) out.bookingRef = bookingRef
  return out
}

// extractNbVoyageurs(raw) → number | null (Lot 2).
// Somme adultes + enfants ; null si aucune des deux clés n'est un nombre fini
// (accepte les strings numériques type "2"). Jamais 0 par défaut.
function extractNbVoyageurs(raw) {
  if (!raw || typeof raw !== 'object') return null
  const a = Number(raw.numAdult)
  const c = Number(raw.numChild)
  const hasA = Number.isFinite(a)
  const hasC = Number.isFinite(c)
  if (!hasA && !hasC) return null
  return (hasA ? a : 0) + (hasC ? c : 0)
}

// b) filterFacturable(rows)
// Exclut les annulées (colonne OU raw_payload.status), les statuts incohérents
// ET source='direct' (le direct interne vit déjà dans pricing_blocks via
// calcDirectInternePure — double comptage sinon ; cf. finding pessimist M1).
// F4 : ne garde QUE les sources prouvées (airbnb/booking). Les sources tierces
// (vrbo/website/inconnu) sont exclues ici et remontées en skipped par
// getResasForMonth (pas de calcul silencieux sans preuve facture).
function filterFacturable(rows) {
  if (!Array.isArray(rows)) return []
  return rows.filter((r) => {
    if (!r) return false
    if (classifyStatus(r) !== 'facturable') return false
    const source = (r.source || '').toLowerCase()
    if (source === 'direct') return false
    if (!VALIDATED_SOURCES.has(source)) return false
    return true
  })
}

// c) getResasForMonth(rows, monthKey, { cleaningFeeDefault, override })
//    → { resas, skipped }   (SHAPE M3.0 — breaking vs M2 qui renvoyait un tableau)
//
// Filtre + impute chaque résa à son mois via le helper EXISTANT
// computeInvoiceMonthByNights (RÉUTILISÉ — exigence pessimist). Toute row écartée
// pour une raison NON triviale (annulée, statut incohérent, source non validée,
// montant corrompu) est remontée dans skipped[] pour décision manuelle ; le
// `direct` reste exclu SILENCIEUSEMENT (vit dans pricing_blocks).
//
// skipped[] entry : { reason, ctx, ...payload selon le cas } où reason ∈
//   'cancelled_decision_manuelle' | 'statut_incoherent' | 'source_non_validee'
//   | 'montant_corrompu'.
function getResasForMonth(rows, monthKey, opts = {}) {
  const resas = []
  const skipped = []
  if (!Array.isArray(rows)) return { resas, skipped }

  for (const row of rows) {
    if (!row) continue

    const source = (row.source || '').toLowerCase()
    const checkIn = row.start_date || null
    const checkOut = row.end_date || null

    // Imputation mois (même règle « majorité de nuits » ADR-182 que l'import
    // CSV). Fallback mois du check-in si helper null. On la calcule AVANT le
    // filtrage afin de n'imputer au mois cible que les rows concernées.
    const invoiceMonth =
      computeInvoiceMonthByNights(checkIn, checkOut) ||
      (typeof checkIn === 'string' ? checkIn.slice(0, 7) : null)
    if (invoiceMonth !== monthKey) continue

    // 1) direct → exclu SILENCIEUSEMENT (pricing_blocks). Pas de skipped.
    if (source === 'direct') continue

    // 2) Annulées + statuts menteurs (garde défensive A.3).
    const cls = classifyStatus(row)
    if (cls === 'cancelled') {
      // Montants V2 d'une cancelled = PÉRIMÉS (Beds24 réécrit l'item à 0 au
      // cancelTime) → indicatifs UNIQUEMENT (affichage écran décision), jamais
      // facturés. menage_prefill = pré-remplissage forfait/2 (PAS une facturation).
      const cleaningFeeDefault = opts.cleaningFeeDefault
      const menagePrefill =
        cleaningFeeDefault != null && Number.isFinite(Number(cleaningFeeDefault))
          ? round2(Number(cleaningFeeDefault) / 2)
          : null
      skipped.push({
        reason: 'cancelled_decision_manuelle',
        ctx: buildCtx(row),
        montants_indicatifs: {
          total_amount: row.total_amount != null ? row.total_amount : null,
          ota_commission: row.ota_commission != null ? row.ota_commission : null,
          city_tax: row.city_tax != null ? row.city_tax : null,
        },
        menage_prefill: menagePrefill,
      })
      continue
    }
    if (cls === 'incoherent') {
      skipped.push({ reason: 'statut_incoherent', ctx: buildCtx(row) })
      continue
    }

    // 3) F4 — source tierce non validée (≠ airbnb/booking, ≠ direct déjà géré).
    if (!VALIDATED_SOURCES.has(source)) {
      skipped.push({ reason: 'source_non_validee', ctx: buildCtx(row) })
      continue
    }

    // 4) F1 — transformation enveloppée : un throw (montant corrompu, etc.)
    //    ne fait PAS planter le mois → skipped 'montant_corrompu'.
    try {
      resas.push(transformV2Resa(row, opts))
    } catch (e) {
      skipped.push({
        reason: 'montant_corrompu',
        ctx: buildCtx(row),
        message: e && e.message ? e.message : String(e),
      })
    }
  }

  return { resas, skipped }
}

// d) matchKvResa(v2Row, kvResas)
// Rapprochement anti-doublon V2 ↔ KV : match par apiReference (airbnbCode pour
// Airbnb, bookingRef pour Booking) prioritaire, fallback (checkIn, plateforme)
// — mêmes conventions identité que les parsers CSV.
//
// ⚠️ CONTRAINTE CÂBLAGE M3.2 (F2) : `kvResas` DOIT être scopé PAR LOGEMENT
// (les résas KV du seul logement concerné). Le fallback (checkIn, plateforme)
// est volontairement large et NE DOIT JAMAIS servir à un ÉCRASEMENT de données :
// il sert uniquement à DÉTECTER un doublon probable. Tout écrasement/merge
// destructif côté câblage doit être réservé aux matchs par CODE STABLE
// (airbnbCode / bookingRef), jamais au fallback (risque de collision entre deux
// résas distinctes au même check-in/plateforme dans un même logement).
// Retourne la résa KV matchée ou null.
function matchKvResa(v2Row, kvResas) {
  if (!v2Row || !Array.isArray(kvResas) || kvResas.length === 0) return null
  const source = (v2Row.source || '').toLowerCase()
  const raw = v2Row.raw_payload || {}
  const apiReference =
    raw.apiReference != null ? String(raw.apiReference).trim() : ''
  const checkIn = v2Row.start_date || null

  // 1) Match par code stable (apiReference) — seul match autorisé à servir un
  //    écrasement côté câblage.
  if (apiReference) {
    if (source === 'airbnb') {
      const m = kvResas.find((r) => r && r.airbnbCode === apiReference)
      if (m) return m
    } else if (source === 'booking') {
      const m = kvResas.find((r) => r && r.bookingRef === apiReference)
      if (m) return m
    }
  }

  // 2) Fallback (checkIn, plateforme) — DÉTECTION de doublon uniquement, jamais
  //    un écrasement (cf. JSDoc / contrainte M3.2 ci-dessus).
  if (checkIn) {
    const m = kvResas.find(
      (r) =>
        r &&
        (r.checkIn || r.date) === checkIn &&
        (r.plateforme || '').toLowerCase() === source
    )
    if (m) return m
  }

  return null
}

module.exports = {
  transformV2Resa,
  filterFacturable,
  getResasForMonth,
  matchKvResa,
  resolveMenage,
  // Helpers exposés (testés isolément).
  round2,
  extractCityTaxFromItems,
  extractMenageFromItems,
  classifyStatus,
}
